import { useState, type ComponentProps, type ReactNode } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import {
  Ai21LabsWordmark,
  BrandPanel,
  Button,
  SplitScreenLayout,
  type BrandPanelProps,
  type SplitScreenGround,
  type SplitScreenLayoutProps,
} from "../design-system";
import { Orb } from "../components/layout/Orb";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import harnessBg from "../design-system/assets/apps/harness-bg.jpg";
import harnessJourney from "../design-system/assets/apps/harness-journey.gif";
import { PRODUCT_NAME, useAuthBrand, type AuthBrand } from "./authBrand";

/* Shared chrome for the sign-in / sign-up screens: the ai21 [product] lockup,
 * the photo left panel, and the provider mark icons. All auth screens render
 * inside SplitScreenLayout (the shared split-screen frame), so the branded
 * left panel is common to every one of them. The product is the Gateway
 * unless the viewer was headed to the Harness Optimizer (see authBrand.ts). */

/** Left-panel content per brand. The Gateway keeps BrandPanel's defaults
 *  (shared with the onboarding wizard, OPSS-22); the Harness variant follows
 *  the "Harness Optimizer" Claude Design sign-in screen (OPSS-487): the
 *  journey GIF at 76px, screen-blended onto the photo and nudged 10px left
 *  so its visible mark lines up with the wordmark. */
const BRAND_PANEL: Record<AuthBrand, BrandPanelProps> = {
  gateway: { visual: <Orb size={56} color="#FFFFFF" /> },
  harness: {
    label: PRODUCT_NAME.harness,
    title: "Find your agent's sweet spot",
    subtitle:
      "Optimize cost, quality, and latency automatically within your constraints.",
    visual: (
      <img
        src={harnessJourney}
        alt=""
        width={76}
        height={76}
        className="block"
        style={{ mixBlendMode: "screen", marginLeft: "-10px" }}
      />
    ),
  },
};

/** The Harness design's warm photo under its darkening gradient; the Gateway
 *  keeps SplitScreenLayout's leaf default (`undefined`). */
const GROUND: Record<AuthBrand, SplitScreenGround | undefined> = {
  gateway: undefined,
  harness: {
    image: harnessBg,
    color: "#41232d",
    overlay: "linear-gradient(to bottom, rgba(20,3,9,0.25), rgba(20,3,9,0.45))",
  },
};

/** The split-screen frame every auth screen renders in: the brand's ground
 *  plus AuthLeftPanel, so screens only supply the card content. */
export function AuthFrame(
  props: Omit<SplitScreenLayoutProps, "leftPanel" | "ground">,
) {
  return (
    <SplitScreenLayout
      leftPanel={<AuthLeftPanel />}
      ground={GROUND[useAuthBrand()]}
      {...props}
    />
  );
}

/** The left panel shared by every auth screen inside SplitScreenLayout.
 *  Renders the shared BrandPanel so the auth and onboarding left panels stay
 *  identical for the Gateway (single source of truth, OPSS-22), swapping the
 *  copy and visual when the viewer came from the Harness Optimizer. Every auth
 *  screen renders it, so it also owns the tab title for the brand. */
export function AuthLeftPanel() {
  const brand = useAuthBrand();
  useDocumentTitle(`AI21 ${PRODUCT_NAME[brand]}`);

  return <BrandPanel {...BRAND_PANEL[brand]} />;
}

/** The ai21 wordmark plus the bracketed section label, matching the brand
 *  convention. `tone` swaps the colors for placement on light vs. dark; the
 *  auth screens use "dark" (wordmark in fg-1 on the surface). */
export function Lockup({ tone }: { tone: "light" | "dark" }) {
  const wordmark = tone === "light" ? "text-grey-0" : "text-fg-1";
  const label = tone === "light" ? "text-grey-0/70" : "text-fg-3";
  const product = PRODUCT_NAME[useAuthBrand()];
  return (
    <div className="flex items-center gap-3" aria-label={`AI21 [${product}]`}>
      <Ai21LabsWordmark className={`block h-[18px] w-auto ${wordmark}`} />
      <span
        className={`select-none whitespace-nowrap font-logo text-body-sm font-medium tracking-[0.4px] ${label}`}
      >
        [ {product} ]
      </span>
    </div>
  );
}

/** The right-panel form block shared by every auth screen — no border or
 *  extra surface (or padding of its own), so it sits directly on the
 *  split-screen's light card, matching the onboarding wizard. */
export function AuthCard({ children }: { children: ReactNode }) {
  return <div className="w-full animate-ai21-rise">{children}</div>;
}

/** The heading shared by every auth screen — the design's 26px semibold Aeonik
 *  Pro title. The `.ai21-brand .t-h3` scope supplies the display face but pins
 *  font-weight to 500, so the size + semibold weight are set inline (higher
 *  specificity than the class rule) to hit the design's exact values. */
export function AuthHeading({ children }: { children: ReactNode }) {
  return (
    <h2
      className="t-h3 leading-tight"
      style={{ color: "var(--fg-1)", fontSize: "26px", fontWeight: 600 }}
    >
      {children}
    </h2>
  );
}

/** Primary auth CTA — the guideline's dark plum button with light text on the
 *  light card, identical to the onboarding wizard's WizardCta. Based on the
 *  shared Button `secondary` variant (neutral border + disabled handling) with
 *  the enabled bg/text overridden via the --onb-cta-* tokens. */
export function AuthCta({
  style,
  disabled,
  ...rest
}: ComponentProps<typeof Button>) {
  return (
    <Button
      variant="secondary"
      size="lg"
      disabled={disabled}
      style={
        disabled
          ? style
          : {
              background: "var(--onb-cta-bg)",
              color: "var(--onb-cta-fg)",
              borderColor: "var(--onb-cta-bg)",
              ...style,
            }
      }
      {...rest}
    />
  );
}

/** Shared input class for the auth forms. `hasErr` swaps to the error skin.
 *  Used by the (currently commented-out) email forms in Login/Signup — kept
 *  here so those forms stay restorable. */
export function authInputClass(hasErr: boolean) {
  return (
    "h-11 w-full rounded-md border bg-surface px-3 text-body text-fg-1 " +
    "placeholder:text-fg-3 outline-none transition-colors duration-fast " +
    "ease-ai21-standard focus:border-focus focus:shadow-focus " +
    "disabled:cursor-not-allowed disabled:bg-surface-muted " +
    (hasErr ? "border-error-border bg-error-bg" : "")
  );
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Sign-in-wrap terms notice, shown on BOTH auth screens directly under the
 *  auth actions: acceptance rides the action itself ("By continuing…"), so no
 *  checkbox gate is needed — and since social auth can't tell sign-up from
 *  sign-in until the redirect completes, the login screen needs it too (a
 *  first-time "Continue with Google" there provisions a new account). */
export function TermsNotice() {
  const link = "font-medium no-underline hover:underline";
  return (
    <p className="text-center text-[12px]" style={{ color: "var(--fg-3)" }}>
      By continuing, you agree to AI21&apos;s{" "}
      <a
        href="https://www.ai21.com/terms-policies/terms-of-use/"
        target="_blank"
        rel="noopener noreferrer"
        className={link}
        style={{ color: "var(--fg-1)" }}
      >
        Terms of Use
      </a>{" "}
      and{" "}
      <a
        href="https://www.ai21.com/terms-policies/privacy-policy/"
        target="_blank"
        rel="noopener noreferrer"
        className={link}
        style={{ color: "var(--fg-1)" }}
      >
        Privacy Policy
      </a>
      .
    </p>
  );
}

/* Shared form fields for the auth screens. They wrap a labelled input styled
 * with authInputClass (the larger, branded auth skin — not the DS TextField,
 * which is the denser editor-form input and has no password-toggle slot). */

interface AuthFieldProps {
  id: string;
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email";
  error?: string;
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

export function AuthField({
  id,
  label,
  value,
  onChange,
  type = "text",
  error,
  placeholder,
  autoComplete,
  disabled,
  autoFocus,
}: AuthFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-body-sm font-medium text-fg-1">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={authInputClass(!!error)}
      />
      {error && (
        <p id={`${id}-error`} className="text-body-sm text-error-fg">
          {error}
        </p>
      )}
    </div>
  );
}

interface PasswordFieldProps {
  id: string;
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Optional node rendered below the input (e.g. a strength meter). */
  children?: ReactNode;
}

export function PasswordField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  autoComplete = "current-password",
  disabled,
  autoFocus,
  children,
}: PasswordFieldProps) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-body-sm font-medium text-fg-1">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={`${authInputClass(!!error)} pr-10`}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-fg-3 outline-none transition-colors hover:text-fg-1 focus-visible:text-fg-1 [&_svg]:size-5"
          aria-label={show ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {show ? <EyeSlash /> : <Eye />}
        </button>
      </div>
      {children}
      {error && (
        <p id={`${id}-error`} className="text-body-sm text-error-fg">
          {error}
        </p>
      )}
    </div>
  );
}

/* Google mark icon, taken verbatim from the design so the social button carries
 * the recognizable brand colors (Phosphor's GoogleLogo is monochrome). Sized to
 * 18px to match the design. */

export function GoogleColorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
